import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useAuth from "../context/useAuth";
import apiFetch from "../api/apiFetch";
import { clearApiCache } from "../api/cache";
import { clearTokens } from "../utils/cookies";
import ConfirmModal from "./ConfirmModal";
import Icon from "./Icon";

const adminMenu = [
  { name: "Dashboard", icon: "bi-grid-1x2", path: "/admin/dashboard" },
  {
    name: "Employees",
    icon: "bi-people",
    path: "/admin/employees",
    matchPaths: ["/admin/employees", "/admin/profile"],
    roles: [1],
  },
  {
    name: "Leaves",
    icon: "bi-calendar-minus",
    path: "/admin/leaves",
    roles: [1, 2],
  },
  {
    name: "Attendance",
    icon: "bi-calendar-week",
    path: "/admin/attendance",
    roles: [1, 2],
  },
  {
    name: "Appreciation",
    icon: "bi-chat-heart",
    path: "/admin/appreciation",
    roles: [1, 2],
  },
  {
    name: "Holiday",
    icon: "bi-gift",
    path: "/admin/holiday",
    matchPaths: ["/admin/holiday", "/admin/create-holidays"],
    roles: [1, 2],
  },
];

const userMenu = [
  { name: "Home", icon: "bi-house", path: "/home" },
  { name: "Employees", icon: "bi-people", path: "/employees" },
  { name: "Leaves", icon: "bi-calendar-minus", path: "/leave" },
  { name: "Attendance", icon: "bi-calendar-week", path: "/attendance" },
  { name: "Appreciation", icon: "bi-chat-heart", path: "/appreciation" },
  { name: "Holiday", icon: "bi-gift", path: "/holidays" },
  { name: "Calendar", icon: "bi-calendar-event", path: "/calendar" },
  { name: "Birthday & Anniversary", icon: "bi-cake2", path: "/birthday" },
  { name: "Task Report", icon: "bi-clipboard2-data", path: "/taskreport" },
];

const Sidebar = ({ isOpen = false, onClose = null }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, clearUser } = useAuth();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleLogout = async () => {
    try {
      await apiFetch("api/auth/logoff", { method: "POST" }, clearUser);
    } catch {
      // ignore network errors
    } finally {
      clearTokens();
      clearApiCache();
      clearUser();
      navigate("/", { replace: true });
    }
  };

  const role = Number(user?.role);
  const isAdmin = role === 1 || role === 2;
  const menu = isAdmin
    ? adminMenu.filter((item) => !item.roles || item.roles.includes(role))
    : userMenu;

  return (
    <>
      <div
        className={`sidebar-backdrop ${isOpen ? "sidebar-backdrop--open" : ""}`}
        onClick={onClose}
      />
      <div className={`admin-sidebar ${isOpen ? "admin-sidebar--open" : ""}`}>
        <div className="sidebar-menu mt-3">
          {menu.map((item) => {
            const matchPaths = item.matchPaths || [item.path];
            const isActive = matchPaths.some((p) =>
              location.pathname.startsWith(p),
            );
            return (
              <Link
                key={item.name}
                to={item.path}
                onClick={onClose}
                className={`d-flex align-items-center text-decoration-none ${isActive ? "active" : ""}`}
              >
                <Icon
                  name={item.icon}
                  className="me-2"
                  style={{ fontSize: "15px" }}
                />
                {item.name}
              </Link>
            );
          })}
        </div>
        <div className="sidebar-menu">
          <button
            type="button"
            onClick={() => setShowLogoutConfirm(true)}
            className="sidebar-logout-btn"
            aria-label="Logout"
          >
            <Icon
              name="box-arrow-right"
              className="me-2"
              style={{ fontSize: "15px" }}
            />
            Logout
          </button>
        </div>
      </div>

      <ConfirmModal
        show={showLogoutConfirm}
        title="Log out?"
        message="Are you sure you want to log out of your account?"
        confirmLabel="Yes, log out"
        cancelLabel="Cancel"
        variant="warning"
        onConfirm={() => {
          setShowLogoutConfirm(false);
          handleLogout();
        }}
        onCancel={() => setShowLogoutConfirm(false)}
      />
    </>
  );
};

export default Sidebar;
